<template>
  <Card class="shipment-financials-section-card">
    <template #content>
      <Section
        :id="id"
        :header="t('shipments.financials.shipmentFinancials', 2)"
        class="shipment-financials-section p-0"
        data-class="flex flex-column"
      >
        <template #headerRight>
          <ShipmentFinancialsActionButton
            :shipment-financials="shipmentFinancials"
            @view-cost-allocation="onViewCostAllocation"
            @create-invoice="createInvoice"
            @refresh="resyncShipmentFinancials"
          />
        </template>

        <MessageContainer :messages="messages" message-class="rygen-message" />

        <ShipmentInvoicesSectionLoadingCard v-if="showLoadingState" />
        <template v-else-if="shipmentFinancials.data?.invoices?.length">
          <ShipmentInvoiceRatesTable
            v-for="invoice in shipmentFinancials.data?.invoices"
            :key="invoice.id"
            class="rounded-top-corners rounded-bottom-corners border-1 border-color-divider"
            :invoice="invoice"
            :perspective="shipmentFinancials.data?.perspective"
            :domain-natural-id="selectedDomain?.naturalId"
            :group-by-category="isAccessorialCategoryEnabled"
          />
        </template>

        <ShipmentLedgerLoadingCard v-if="showLoadingState" />
        <ShipmentLedgerView
          v-else
          style="box-shadow: none"
          class="flex-1"
          :shipment-financials="shipmentFinancials.data"
        />
      </Section>

      <ShipmentFinancialsCostAllocationModal
        v-model:visible="costAllocationModalVisible"
        :shipment="shipment"
        :shipment-financials="shipmentFinancials.data"
      />
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import { computed, h, PropType, ref, toRefs, watch } from 'vue';
import { I18nT, useI18n } from 'vue-i18n';

import { useUid } from '@rygen/rygen-ui/composables';
import environmentPropertiesStore from '@rygen/rygen-ui/env/EnvironmentPropertiesStore';
import { ErrorResponse, Optional } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useShipmentFinancials } from '@/composables';
import { MessageSpecification, Order, Shipment } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import ShipmentFinancialsCostAllocationModal from '@/components/shipmentFinancials/costAllocation/ShipmentFinancialsCostAllocationModal.vue';
import ShipmentFinancialsActionButton from '@/components/shipmentFinancials/ShipmentFinancialsActionButton.vue';
import ShipmentInvoicesSectionLoadingCard from '@/components/shipmentFinancials/ShipmentInvoicesSectionLoadingCard.vue';
import ShipmentLedgerLoadingCard from '@/components/shipmentFinancials/ShipmentLedgerLoadingCard.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';
import ShipmentLedgerView from '@rygen/rygen-ui/components/shipment/financials/ledger/ShipmentLedgerView.vue';
import ShipmentInvoiceRatesTable from '@rygen/rygen-ui/components/shipment/financials/ShipmentInvoiceRatesTable.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Card from 'primevue/card';

const MISSING_FINANCIAL_SETTINGS_MESSAGE =
  'Missing service agreement or pay file setting';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('financials').uid,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  relatedOrders: {
    type: Array as PropType<Order[]>,
    default: () => [],
  },
});
const { id, shipment } = toRefs(props);

const { isRygenDirectClient, selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const isAccessorialCategoryEnabled = useFlag('accessorial-category-feat-flag');
const { loadShipmentFinancials, resyncShipmentFinancials, shipmentFinancials } =
  useShipmentFinancials(shipment);

const canCreateShipmentSetting = ref(false);
const costAllocationModalVisible = ref(false);

const showLoadingState = computed(() => {
  return (
    shipmentFinancials.value.loading ||
    (!shipmentFinancials.value.data && !shipmentFinancials.value.error)
  );
});

const isMissingFinancialSettings = computed(() => {
  if (!shipmentFinancials.value.error) {
    return false;
  }

  let error = shipmentFinancials.value.error;
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const errorResponse = error.response?.data as ErrorResponse;
  return errorResponse.exceptionMessage === MISSING_FINANCIAL_SETTINGS_MESSAGE;
});

const financialSettingsParam = computed(() => {
  return isRygenDirectClient.value
    ? t('shipments.financials.payFileSettings')
    : t('shipments.financials.serviceAgreements');
});

const financialSettingsLink = computed(() => {
  const billingUrl = environmentPropertiesStore.getValue('BILLING_URL');
  const domainSuffix = selectedDomain.value?.naturalId
    ? `?domain-context=${encodeURIComponent(selectedDomain.value.naturalId)}`
    : '';
  return isRygenDirectClient.value
    ? `${billingUrl}pay-file/setting${domainSuffix}`
    : `${billingUrl}service-agreement/create${domainSuffix}`;
});

const messages = computed(() => {
  const messageSpecifications: MessageSpecification[] = [
    {
      id: 'missing-param-with-link-warning',
      textSlot: () => {
        return h(
          I18nT,
          {
            keypath: 'shipments.financials.errors.missingParamWithLink',
            tag: 'span',
          },
          {
            param: () => financialSettingsParam.value,
            link: () =>
              h('a', { href: financialSettingsLink.value }, t('common.here')),
          },
        );
      },
      severity: 'warn',
      visible:
        isMissingFinancialSettings.value && canCreateShipmentSetting.value,
      closable: true,
    },
    {
      id: 'missing-param-warning',
      text: t('shipments.financials.errors.missingParam', {
        param: financialSettingsParam.value,
      }),
      severity: 'warn',
      visible:
        isMissingFinancialSettings.value && !canCreateShipmentSetting.value,
      closable: true,
    },
    {
      id: 'generic-error',
      text: t('errors.loadingParam', {
        param: t('shipments.financials.financials', 2),
      }),
      severity: 'warn',
      visible:
        !!shipmentFinancials.value.error && !isMissingFinancialSettings.value,
      closable: true,
    },
  ];

  return messageSpecifications;
});

const onViewCostAllocation = () => {
  costAllocationModalVisible.value = true;
};

const createInvoice = () => {
  if (!shipment.value.id) {
    console.error('Cannot create invoice: shipment ID is missing');
    return;
  }

  const billingBaseUrl = environmentPropertiesStore.getValue('BILLING_URL');

  const domainParam = selectedDomain.value?.naturalId
    ? `&domain-context=${encodeURIComponent(selectedDomain.value.naturalId)}`
    : '';
  const url = `${billingBaseUrl}carrier/invoice/create?strategy=SHIPMENT&sourceSystem=CORSAIR&sourceSystemId=${shipment.value.id}${domainParam}`;
  window.location.href = url;
};

const loadCanCreateShipmentSettingPermission = async (
  shipmentId: NonNullable<Shipment['id']>,
) => {
  try {
    canCreateShipmentSetting.value =
      await api.user.getCanCreateFinancialSettingPermission(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading canCreateFinancialSetting permission', e);
  }
};

watch(
  shipment,
  (newVal: Shipment, oldVal: Optional<Shipment>) => {
    const isSameId = newVal.id === oldVal?.id;
    const isSameOwner = newVal.owner?.id === oldVal?.owner?.id;
    if (!newVal.id || (isSameId && !isSameOwner)) {
      return;
    }

    loadCanCreateShipmentSettingPermission(newVal.id);
    loadShipmentFinancials();
  },
  { immediate: true },
);

defineExpose({
  loadShipmentFinancials,
});
</script>

<style>
.shipment-financials-section-card.p-card {
  & > .p-card-body {
    padding: 0;

    .shipment-financials-section {
      .section-header {
        padding: var(--sp-6) var(--sp-6) 0;
      }

      .section-data {
        .message-container {
          padding-inline: var(--sp-6);
          padding-bottom: var(--sp-3);
        }

        .shipment-invoice {
          border-left: none;
          border-right: none;
        }

        .shipment-invoice-loading {
          padding: 0 var(--sp-6);
        }

        .shipment-ledger {
          box-shadow: none;
          border-left: none;
          border-right: none;
          border-bottom: none;
        }

        .shipment-ledger-loading {
          padding: var(--sp-12) var(--sp-6) var(--sp-6);
        }
      }
    }
  }
}
</style>
